import { AxiosError } from 'axios'
import { createStandardAction } from 'typesafe-actions'
import { AnyAction } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'

import { htmlDecode } from '../../utils/decoding'
import { HOSPITALS_URL } from '../../constants/urls/backend'
import { IRequestResult } from '../types'
import { apiRoute } from '../../utils/routeUtils'
import { localeMoment } from '../../utils/localeMoment'
import { axiosWithAdal } from '../../utils/http'
import { IHospital, IHospitalToUpdate } from '../../types/hospital'
import { IChangedSubscription } from '../../types/subscription'

const hospitalsSuccess = createStandardAction('@@hospitals/SUCCESS')<IHospital[]>()
const hospitalsError = createStandardAction('@@hospitals/ERROR')<string[]>()
const hospitalsRequest = createStandardAction('@@hospitals/REQUEST')()

const hospitalUpdateSuccess = createStandardAction('@@hospitals/UPDATE_SUCCESS')<IHospital>()
const hospitalUpdateError = createStandardAction('@@hospitals/UPDATE_ERROR')<string[]>()
const hospitalUpdateRequest = createStandardAction('@@hospitals/UPDATE_REQUEST')()

interface IResponse {
  id: number
  instance_id: number
  installation: number
  name: string
  max_enabled_clinicians: number
  created: string
  updated: string
  timezone: string
}

const fetchHospitals = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
    dispatch(hospitalsRequest())
    try {
      const response = await axiosWithAdal.get<IResponse[]>(apiRoute(HOSPITALS_URL), {
        headers: {
          'Content-Type': 'application/json',
        }
      })
      const payload: IHospital[] = response.data.map(x => {
        return {
          id: x.id,
          created: localeMoment(x.created),
          updated: localeMoment(x.updated),
          instanceId: x.instance_id,
          installation: x.installation,
          name:  htmlDecode(x.name),
          maxEnabledClinicians: x.max_enabled_clinicians,
          timezone: x.timezone
        }
      })

      dispatch(hospitalsSuccess(payload))
    } catch (e) {
      dispatch(hospitalsError([ e.message ]))
      return
    }
  }
}

const updateHospital = (
  hospital: IHospitalToUpdate,
  subscriptions?: IChangedSubscription[]
): ThunkAction<Promise<IRequestResult>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<IRequestResult> => {
    interface IPayload {
      name: string
      installation: number
      instance_id: number
      timezone: string
      subscriptions: Array<{
        id: number | null
        plan: number
        start: string
        end: string
      }>
      max_enabled_clinicians: number
    }

    // If you pass an empty array of subscriptions, they will all be removed for the hospital.
    // TODO: Decouple subscriptions from hospitals and use subscription endpoints instead.
    const payload: IPayload = {
      name: hospital.name,
      installation: hospital.installation,
      instance_id: hospital.instanceId,
      timezone: hospital.timezone || 'UTC',
      subscriptions: (subscriptions! || hospital.subscriptions).map(s => ({
        ...s,
        plan: s.planId,
        start: s.start.utc().format(),
        end: s.end.utc().format()
      })),
      max_enabled_clinicians: hospital.maxEnabledClinicians
    }

    dispatch(hospitalUpdateRequest())
    try {
      const response = await axiosWithAdal.put<IResponse>(
        `${apiRoute(HOSPITALS_URL)}${hospital.id}`, JSON.stringify(payload), {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      dispatch(hospitalUpdateSuccess({
        id: response.data.id,
        name: response.data.name,
        timezone: response.data.timezone,
        created: localeMoment(response.data.created),
        updated: localeMoment(response.data.updated),
        installation: response.data.installation,
        instanceId: response.data.instance_id,
        maxEnabledClinicians: response.data.max_enabled_clinicians,
      }))

      return {
        status: response.status,
        message: response.statusText,
      }
    } catch (e) {
      dispatch(hospitalUpdateError([ e.message ]))
      const error = e as AxiosError
      return {
        status: error.code ? parseInt(error.code, 10) : 500,
        message: error.message
      }
    }
  }
}

export {
  fetchHospitals,
  updateHospital,
  hospitalsSuccess,
  hospitalsError,
  hospitalsRequest,
  hospitalUpdateSuccess,
  hospitalUpdateError,
  hospitalUpdateRequest,
}
